import { useEffect } from "react";
import { ArrowRight, MapPin, Clock, Phone } from "lucide-react";

const Img = ({ src, alt, className }) => (
  <img src={src} alt={alt} loading="lazy" className={className} />
);

const DemoSite = ({ cfg }) => {
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    document.title = `${cfg.name} — Demo Website Concept`;
    return () => document.head.removeChild(meta);
  }, [cfg.name]);

  return (
    <div className="min-h-screen bg-midnight text-white" data-testid={`demo-${cfg.slug}`}>
      {/* nav */}
      <div className="border-b border-white/10 bg-midnight/90">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <p className="font-sora font-extrabold tracking-tight text-lg">{cfg.name}</p>
          <div className="hidden md:flex items-center gap-8 text-sm text-white/60">
            <span>Menu</span><span>About</span><span>Locations</span><span>Contact</span>
            <span className={`rounded-full ${cfg.accentBg} ${cfg.accentTextOn} px-5 py-2 text-sm font-bold`}>
              Book a Table
            </span>
          </div>
        </div>
      </div>

      {/* hero */}
      <section className="relative min-h-[80vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <Img src={cfg.heroImg} alt={`${cfg.name} interior`} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-midnight via-midnight/55 to-midnight/20" />
        </div>
        <div className="relative max-w-6xl mx-auto px-6 pb-20 pt-40 w-full">
          <p className={`text-xs uppercase tracking-[0.3em] font-semibold ${cfg.accentText}`}>{cfg.tagline}</p>
          <h1 className="font-sora font-extrabold tracking-tighter text-5xl sm:text-7xl leading-[0.98] mt-5 max-w-3xl">
            {cfg.headline}
          </h1>
          <p className="mt-5 max-w-xl text-white/65 leading-relaxed">{cfg.sub}</p>
          <div className="mt-9 flex flex-wrap gap-4">
            <span className={`inline-flex items-center gap-2 rounded-full ${cfg.accentBg} ${cfg.accentTextOn} px-7 py-3.5 font-bold`}>
              View the Menu <ArrowRight className="w-5 h-5" />
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border border-white/25 px-7 py-3.5 font-semibold text-white/85">
              <Phone className="w-4 h-4" /> Reserve
            </span>
          </div>
        </div>
      </section>

      {/* menu teaser */}
      <section className="max-w-6xl mx-auto px-6 py-24">
        <p className={`text-xs uppercase tracking-[0.3em] font-semibold ${cfg.accentText}`}>From the Kitchen</p>
        <h2 className="font-sora font-extrabold tracking-tighter text-3xl sm:text-5xl mt-4">House favourites</h2>
        <div className="mt-12 grid sm:grid-cols-3 gap-6">
          {cfg.menu.map((m) => (
            <div key={m.name} className="rounded-2xl border border-white/10 bg-surface overflow-hidden">
              <div className="aspect-[4/3] overflow-hidden">
                <Img src={m.img} alt={m.name} className="w-full h-full object-cover" />
              </div>
              <div className="p-6">
                <div className="flex items-baseline justify-between gap-3">
                  <h3 className="font-sora font-bold tracking-tight">{m.name}</h3>
                  <span className={`font-sora font-bold text-sm ${cfg.accentText}`}>${m.price}</span>
                </div>
                <p className="text-sm text-white/50 mt-2 leading-relaxed">{m.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* info band */}
      <section className="border-t border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-16 grid sm:grid-cols-3 gap-8">
          <div className="flex items-start gap-3">
            <MapPin className={`w-5 h-5 mt-0.5 ${cfg.accentText}`} />
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-white/40">Find us</p>
              <p className="text-sm text-white/75 mt-1.5">{cfg.address}</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Clock className={`w-5 h-5 mt-0.5 ${cfg.accentText}`} />
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-white/40">Hours</p>
              <p className="text-sm text-white/75 mt-1.5">{cfg.hours}</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Phone className={`w-5 h-5 mt-0.5 ${cfg.accentText}`} />
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-white/40">Reservations</p>
              <p className="text-sm text-white/75 mt-1.5">Call or book online — walk-ins welcome</p>
            </div>
          </div>
        </div>
      </section>

      {/* footer */}
      <div className="border-t border-white/10 py-8 text-center">
        <p className="font-sora font-bold tracking-tight text-white/80">{cfg.name}</p>
        <p className="text-xs text-white/30 mt-2">Demo website concept by Zilleneal Digital — sample content for presentation purposes.</p>
      </div>
    </div>
  );
};

export const EMBER_CFG = {
  slug: "ember-oak",
  name: "Ember & Oak",
  tagline: "Wood-fired grill · craft cocktails · Alberta",
  headline: "Fire-kissed plates, worth the wait.",
  sub: "A modern grillhouse where everything touches the wood fire — steaks, vegetables, even the cocktails' smoke.",
  heroImg: "https://images.pexels.com/photos/1581384/pexels-photo-1581384.jpeg?auto=compress&cs=tinysrgb&w=1600",
  accentText: "text-amber-400",
  accentBg: "bg-amber-500",
  accentTextOn: "text-midnight",
  address: "Sample address, Edmonton AB",
  hours: "Tue – Sun, 4 PM – 11 PM",
  menu: [
    { img: "https://images.pexels.com/photos/67468/pexels-photo-67468.jpeg?auto=compress&cs=tinysrgb&w=800", name: "Oak-Smoked Ribeye", desc: "45-day aged, finished over birch coals.", price: "46" },
    { img: "https://images.pexels.com/photos/262978/pexels-photo-262978.jpeg?auto=compress&cs=tinysrgb&w=800", name: "The Ember Room", desc: "Chef's counter seating beside the open fire.", price: "85" },
    { img: "https://images.pexels.com/photos/1307698/pexels-photo-1307698.jpeg?auto=compress&cs=tinysrgb&w=800", name: "Smoked Old Fashioned", desc: "Bourbon, maple, oak smoke under glass.", price: "16" },
  ],
};

export const CRUST_CFG = {
  slug: "the-daily-crust",
  name: "The Daily Crust",
  tagline: "Artisan bakery · café · fresh daily",
  headline: "Sourdough worth setting an alarm for.",
  sub: "Small-batch breads and pastries baked before sunrise — with coffee that keeps up.",
  heroImg: "https://images.pexels.com/photos/1267320/pexels-photo-1267320.jpeg?auto=compress&cs=tinysrgb&w=1600",
  accentText: "text-orange-300",
  accentBg: "bg-orange-300",
  accentTextOn: "text-midnight",
  address: "Sample address, Calgary AB",
  hours: "Daily, 7 AM – 3 PM",
  menu: [
    { img: "https://images.pexels.com/photos/941861/pexels-photo-941861.jpeg?auto=compress&cs=tinysrgb&w=800", name: "Country Sourdough", desc: "48-hour ferment, stone-milled flour.", price: "9" },
    { img: "https://images.pexels.com/photos/1449773/pexels-photo-1449773.jpeg?auto=compress&cs=tinysrgb&w=800", name: "Morning Bun", desc: "Croissant dough, cinnamon sugar, orange zest.", price: "5" },
    { img: "https://images.pexels.com/photos/2290753/pexels-photo-2290753.jpeg?auto=compress&cs=tinysrgb&w=800", name: "Café Latte", desc: "Local roast, silky micro-foam.", price: "4.75" },
  ],
};

export default DemoSite;
